import { gsap } from "gsap";

/**
 * @class TopInfoController
 * @description Controller for managing TopInfoContainer - switching between info blocks with fade animation and rotation support
 */
export class TopInfoController {
    /**
     * @param {SuperContainer[]} topInfoContainer - TopInfoContainer from layout
     */
    constructor(topInfoContainer) {
        this.containers = topInfoContainer;
        this.infoElements = {};
        this.currentInfo = null;
        this.rotationTimeline = null;
        this.rotationNames = [];
        this.rotationIndex = 0;
        this.fadeDuration = 1;

        if (this.containers) {
            this.containers.forEach(container => {
                container.children.forEach(child => {
                if (child.name) {
                    if (!this.infoElements[child.name]) {
                        this.infoElements[child.name] = [];
                    }
                    this.infoElements[child.name].push(child);
                    child.alpha = 0;
                        child.visible = false;
                    }
                });
            });
        }
    }

    /**
     * @description Show info element by name with fade animation, hide all others
     * @param {string} name - Element name to show
     */
    showInfo(name) {
        this.stopRotation();

        this._fadeToElement(name);
    }

    /**
     * @description Fade to specific element without stopping rotation
     * @param {string} name - Element name to show
     * @private
     */
    _fadeToElement(name) {
        const targetElements = this.infoElements[name];
        if (!targetElements.length) return;

        Object.keys(this.infoElements).forEach(elementName => {
            const elements = this.infoElements[elementName];
            if (!elements.length) return;

            elements.forEach(element => {
            if (elementName === name) {
                element.visible = true;
                gsap.killTweensOf(element);
                gsap.to(element, {
                    alpha: 1,
                    duration: this.fadeDuration,
                    ease: "power2.inOut"
                });
            } else if (element.visible && element.alpha > 0) {
                gsap.killTweensOf(element);
                gsap.to(element, {
                    alpha: 0,
                    duration: this.fadeDuration,
                    ease: "power2.inOut",
                    onComplete: () => {
                        element.visible = false;
                        }
                    });
                }
            });
        });

        this.currentInfo = name;
    }

    /**
     * @description Start rotation of info elements using GSAP timeline
     * @param {string[]} names - Array of element names to rotate
     * @param {number} interval - Interval between switches in milliseconds
     */
    startRotation(names, interval) {
        this.stopRotation();

        if (!names || names.length === 0) return;

        this.rotationNames = names;
        const intervalSec = interval / 1000;

        if (names.length === 1) {
            this._fadeToElement(names[0]);
            return;
        }

        const firstElements = this.infoElements[names[0]];
        if (firstElements?.length) {
            firstElements.forEach(element => {
                element.visible = true;
                element.alpha = 1;
            });
        }
        this.currentInfo = names[0];

        this.rotationTimeline = gsap.timeline({ repeat: -1 });

        const showDuration = intervalSec - this.fadeDuration;

        names.forEach((name, i) => {
            const elements = this.infoElements[name];
            if (!elements?.length) return;

            const position = i * (this.fadeDuration + showDuration);

            elements.forEach(element => {
                const elementTimeline = gsap.timeline();
                
                if (i === 0) {
                    elementTimeline
                        .to(element, { 
                            alpha: 0, 
                            duration: this.fadeDuration, 
                            delay: showDuration,
                            ease: "power2.inOut"
                        })
                        .set(element, { visible: false });
                } else {
                    elementTimeline
                        .set(element, { visible: true })
                        .to(element, { alpha: 1, duration: this.fadeDuration, ease: "power2.inOut" })
                        .to(element, { 
                            alpha: 0, 
                            duration: this.fadeDuration, 
                            delay: showDuration,
                            ease: "power2.inOut"
                        })
                        .set(element, { visible: false });
                }

                this.rotationTimeline.add(elementTimeline, position);
            });
        });

        const firstElementsAgain = this.infoElements[names[0]];
        if (firstElementsAgain?.length) {
            const lastPosition = names.length * (this.fadeDuration + showDuration);
            
            firstElementsAgain.forEach(element => {
                const elementTimeline = gsap.timeline()
                    .set(element, { visible: true })
                    .to(element, { alpha: 1, duration: this.fadeDuration, ease: "power2.inOut" });

                this.rotationTimeline.add(elementTimeline, lastPosition);
            });
        }
    }

    /**
     * @description Stop rotation of info elements
     */
    stopRotation() {
        if (this.rotationTimeline) {
            this.rotationTimeline.kill();
            this.rotationTimeline = null;
        }
        this.rotationNames = [];
        this.rotationIndex = 0;

        Object.keys(this.infoElements).forEach(elementName => {
            const elements = this.infoElements[elementName];
            if (!elements.length) return;

            elements.forEach(element => {
                gsap.killTweensOf(element);
                element.alpha = 0;
                element.visible = false;
            });
        });
    }

    /**
     * @description Hide all info elements
     */
    hideAll() {
        this.stopRotation();

        Object.keys(this.infoElements).forEach(elementName => {
            const elements = this.infoElements[elementName];
            if (!elements.length) return;

            elements.forEach(element => {
            if (element.visible && element.alpha > 0) {
                gsap.killTweensOf(element);
                gsap.to(element, {
                    alpha: 0,
                    duration: this.fadeDuration,
                    ease: "power2.inOut",
                    onComplete: () => {
                        element.visible = false;
                    }
                });
            }
            });
        });

        this.currentInfo = null;
    }
}

